import { OrderListUploadViewStoreV2 } from '@planning/pages/Order/stores/OrderListUploadViewStoreV2'
import { useTranslate } from '@tolgee/react'

export class FileFormatError extends Error {
  constructor(msg: string) {
    super(msg)

    Object.setPrototypeOf(this, FileFormatError.prototype)
  }
}

export class DuplicateRailcarsWithDifferentRailTracksError extends Error {
  constructor(public railcars: string[]) {
    super(`Duplicate railcars with different rail tracks`)

    Object.setPrototypeOf(this, DuplicateRailcarsWithDifferentRailTracksError.prototype)
  }
}
export class DuplicateRailcarsWithDifferentSequenceError extends Error {
  constructor(public railcars: string[]) {
    super(`Duplicate rail cars with different sequence`)

    Object.setPrototypeOf(this, DuplicateRailcarsWithDifferentSequenceError.prototype)
  }
}
export class DuplicateSequencesWithDifferentRailcarsError extends Error {
  constructor(public lineNumbers: number[]) {
    super(`Duplicate sequences with different railcars`)

    Object.setPrototypeOf(this, DuplicateSequencesWithDifferentRailcarsError.prototype)
  }
}

export class IsoCodeValidationError extends Error {
  constructor(public isoCodes: string[]) {
    super(`Found invalid IsoCodes`)

    Object.setPrototypeOf(this, IsoCodeValidationError.prototype)
  }
}

export class DuplicateContainerNumberError extends Error {
  constructor(public containerNumbers: string[]) {
    super(`Duplicate container numbers found`)

    Object.setPrototypeOf(this, DuplicateContainerNumberError.prototype)
  }
}

export class MissingRailtrackError extends Error {
  constructor(public railcars: string[]) {
    super(`Found railcars with missing rail track`)

    Object.setPrototypeOf(this, MissingRailtrackError.prototype)
  }
}

export class RailcarSequenceConflict extends Error {
  constructor(public mismatchedRailcars: string[]) {
    super(`Found mismatched railcars/waggons for the same sequence and track`)
    Object.setPrototypeOf(this, RailcarSequenceConflict.prototype)
  }
}

export class RailcarSequenceMissing extends Error {
  constructor(public missingRailcars: string[]) {
    super(`Found missing railcar sequence numbers for railcars: ${missingRailcars.join(', ')}`)
    Object.setPrototypeOf(this, RailcarSequenceMissing.prototype)
  }
}

export class InvalidRailtrackError extends Error {
  constructor(public railTracks: string[]) {
    super(
      `Found railcars with invalid tracks. Either use the tracks assigned to the visit or assign the missing track`,
    )

    Object.setPrototypeOf(this, InvalidRailtrackError.prototype)
  }
}

export class NotPlannedRailtrackError extends Error {
  constructor(public railTracks: string[]) {
    super(
      `Found railcars with invalid tracks. Either use the tracks assigned to the visit or assign the missing track`,
    )

    Object.setPrototypeOf(this, NotPlannedRailtrackError.prototype)
  }
}

export class OtherLabelsValidationError extends Error {
  constructor(public invalidOtherLabels: string[]) {
    super(`Found invalid 'Other Labels': ${invalidOtherLabels.join(', ')}`)
    Object.setPrototypeOf(this, OtherLabelsValidationError.prototype)
  }
}

export class InvalidOperatorError extends Error {
  constructor(public operators: string[]) {
    super(`Found invalid operators: ${operators.join(', ')}`)

    Object.setPrototypeOf(this, InvalidOperatorError.prototype)
  }
}

export const useOrderListUploadError = (orderListUploadViewStoreV2: OrderListUploadViewStoreV2) => {
  const { t } = useTranslate()

  const handleError = (error: Error) => {
    const { message } = error

    // File format error
    if (error instanceof FileFormatError) {
      orderListUploadViewStoreV2.setUploadFailedReason(
        `${t('fileFormatError', 'File format error')}: ${message}`,
      )
      return
    }

    // ISO Code errors
    if (error instanceof IsoCodeValidationError) {
      orderListUploadViewStoreV2.setUploadFailedReason(
        t('isoCodeValidationError', 'Found invalid IsoCodes: {isoCodes}', {
          isoCodes: error.isoCodes.join(', '),
        }),
      )
      return
    }

    // Railtrack errors
    if (error instanceof MissingRailtrackError) {
      orderListUploadViewStoreV2.setUploadFailedReason(
        t('missingRailtrackError', 'Found railcars with missing rail track: {railcars}', {
          railcars: error.railcars.join(', '),
        }),
      )
      return
    }

    if (error instanceof InvalidRailtrackError) {
      orderListUploadViewStoreV2.setUploadFailedReason(
        t(
          'invalidRailtrackError',
          'The rail track(s) {railTracks} do not exist in the system. Please check your file and the available rail tracks in the system (Rail tracks can be viewed and created in the Layout menu)',
          {
            railTracks: error.railTracks.join(', '),
          },
        ),
      )
      return
    }

    if (error instanceof NotPlannedRailtrackError) {
      orderListUploadViewStoreV2.setUploadFailedReason(
        t(
          'notPlannedRailtrackError',
          'We found rail tracks {railTracks} in your file that have not been assinged to the rail vist. Rail tracks used in your upload must be assigned via rail track planning inside the system beforehand. Please check rail track planning or adjust your file',
          {
            railTracks: error.railTracks.join(', '),
          },
        ),
      )
      return
    }

    // Railcar errors
    if (error instanceof DuplicateRailcarsWithDifferentSequenceError) {
      orderListUploadViewStoreV2.setUploadFailedReason(
        t(
          'duplicatedRailcarsWithDifferentSequenceError',
          'Duplicated railcars with different sequence: {railcars}',
          { railcars: error.railcars.join(', ') },
        ),
      )
      return
    }

    if (error instanceof DuplicateSequencesWithDifferentRailcarsError) {
      orderListUploadViewStoreV2.setUploadFailedReason(
        t(
          'duplicateSequencesWithDifferentRailcarsError',
          'The file you are trying to upload containes several railcars with the same sequence number. Please check lines {lineNumbers} in the csv file',
          { lineNumbers: error.lineNumbers.join(', ') },
        ),
      )
      return
    }

    if (error instanceof DuplicateRailcarsWithDifferentRailTracksError) {
      orderListUploadViewStoreV2.setUploadFailedReason(
        t(
          'duplicateRailcarsWithDifferentRailTracksError',
          'Duplicate railcars with different rail tracks: {railcars}',
          { railcars: error.railcars.join(', ') },
        ),
      )
      return
    }

    if (error instanceof RailcarSequenceConflict) {
      orderListUploadViewStoreV2.setUploadFailedReason(
        t('railcarSequenceConflictDetected', 'Railcar sequence conflict detected: {railcars}', {
          railcars: error.mismatchedRailcars.join(' '),
        }),
      )
      return
    }

    if (error instanceof RailcarSequenceMissing) {
      let message = t('missingRailcarSequenceDetected', 'Missing Railcar sequence detected')
      if (error.missingRailcars.some(railcar => railcar !== '')) {
        message = message.concat(
          t('forRailcarsWithNames', 'for railcars with names: {railcars}', {
            railcars: error.missingRailcars.join(', '),
          }),
        )
      }
      orderListUploadViewStoreV2.setUploadFailedReason(message)
      return
    }

    // Other Labels errors
    if (error instanceof OtherLabelsValidationError) {
      orderListUploadViewStoreV2.setUploadFailedReason(
        t('foundInvalidOtherLabels', "Found invalid 'Other Labels': {invalidOtherLabels}", {
          invalidOtherLabels: error.invalidOtherLabels.join(', '),
        }),
      )
      return
    }

    // Container number errors
    if (error instanceof DuplicateContainerNumberError) {
      orderListUploadViewStoreV2.setUploadFailedReason(
        t(
          'duplicateContainerNumberError',
          'Duplicate container numbers found: {containerNumbers}',
          { containerNumbers: error.containerNumbers.join(', ') },
        ),
      )
      return
    }

    orderListUploadViewStoreV2.setUploadFailedReason(message)
  }

  return { handleError }
}
